import { getAuthorized } from "@/lib/fetch";
import { Autocomplete, AutocompleteItem } from "@heroui/react";
import { useEffect, useState } from "react";
import { FieldValues, Path, PathValue } from "react-hook-form";
import { toast } from "sonner";
import { AutocompleteFieldProps } from "./types";
import { Option } from "../../types/option";

export function AutocompleteField<T extends FieldValues>({
  field,
  setValue,
  watch,
}: {
  field: AutocompleteFieldProps;
  setValue: (name: Path<T>, value: PathValue<T, Path<T>>) => void;
  watch: (name: Path<T>) => any;
}) {
  const selectedValue = watch(field.name as Path<T>) || "";
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Option[]>([]);

  // Fetch options from URL if provided
  useEffect(() => {
    if (field.setOptionsFromUrl) {
      const fetchOptions = async () => {
        try {
          const url = `${import.meta.env.VITE_BACKEND_URL}/${
            field.setOptionsFromUrl
          }`;
          const response = await getAuthorized(url);
          const data = await response.json();

          if (response.status !== 200) throw new Error(data.detail);
          setOptions(data);
        } catch (error) {
          toast.error(`Impossible de récupérer les options`);
          setOptions([]);
        }
      };

      fetchOptions();
    } else if (field.options) {
      setOptions(field.options);
    } else {
      setOptions([]);
    }
  }, [field.setOptionsFromUrl, field.options]);

  // Synchronize the input with the selected value
  useEffect(() => {
    const option = options.find((opt) => opt.value === selectedValue);
    if (option) {
      setInputValue(option.label);
    } else if (!selectedValue) {
      setInputValue("");
    }
  }, [selectedValue, options]);

  return (
    <Autocomplete
      isClearable
      size="sm"
      labelPlacement="outside"
      key={field.name}
      label={field.label}
      selectedKey={selectedValue ? selectedValue.toString() : undefined}
      inputValue={inputValue}
      onInputChange={(value) => {
        setInputValue(value);

        if (!value) {
          setValue(field.name as Path<T>, "" as PathValue<T, Path<T>>);
          return;
        }

        const matchedOption = options.find(
          (option) => option.label.toLowerCase() === value.toLowerCase(),
        );

        if (matchedOption) {
          setValue(
            field.name as Path<T>,
            matchedOption.value as PathValue<T, Path<T>>,
          );
        }
      }}
      onSelectionChange={(key) => {
        if (key) {
          setValue(field.name as Path<T>, key as PathValue<T, Path<T>>);
          const selectedOption = options.find((option) => option.value === key);
          if (selectedOption) {
            setInputValue(selectedOption.label);
          }
        }
      }}
      onClear={() => {
        setValue(field.name as Path<T>, "" as PathValue<T, Path<T>>);
        setInputValue("");
      }}
      inputProps={{
        classNames: {
          inputWrapper:
            "bg-white hover:bg-white hover:border-slate-400 data-[hover=true]:bg-white group-data-[focus=true]:bg-white group-data-[focus=true]:border-primary border-2 shadow-sm",
          label: "text-blue-600 group-data-[filled-within=true]:text-blue-600",
        },
      }}
    >
      {options.map((option) => (
        <AutocompleteItem key={option.value} textValue={option.label}>
          {option.label}
        </AutocompleteItem>
      ))}
    </Autocomplete>
  );
}
